import React, { useEffect, useState } from "react";
import { ArrowLeft, Volume2, MessageSquareReply, Send as SendIcon, CheckCircle2 } from "lucide-react";
import { useClara } from "../contexts/ClaraContext";
import { useData } from "../contexts/DataContext";
import { MESSAGES } from "../data/sampleData";
import Avatar from "../components/Avatar";
import VoiceReply from "../components/VoiceReply";

const formatRelative = (ts) => {
    const diff = Date.now() - ts;
    if (diff < 60_000) return "Just now";
    if (diff < 3600_000) return `${Math.floor(diff / 60_000)}m ago`;
    if (diff < 86_400_000) return `${Math.floor(diff / 3600_000)}h ago`;
    return `${Math.floor(diff / 86_400_000)}d ago`;
};

export const Messages = () => {
    const { speak } = useClara();
    const { repliesByMessage, addReply } = useData();
    const [openId, setOpenId] = useState(null);
    const [replying, setReplying] = useState(false);
    const open = MESSAGES.find((m) => m.id === openId);
    const openReplies = open ? repliesByMessage[open.id] || [] : [];

    useEffect(() => {
        if (!open) {
            speak("Messages. Tap a message and Clara will read it aloud.");
        }
    }, [open, speak]);

    const handleOpen = (msg) => {
        setOpenId(msg.id);
        setReplying(false);
        speak(`Message from ${msg.sender}. ${msg.full}`);
    };

    const handleStartReply = () => {
        if (!open) return;
        setReplying(true);
    };

    const handleSent = (payload) => {
        if (!open) return;
        addReply(open.id, payload.text);
    };

    if (open) {
        return (
            <div className="space-y-4">
                <button
                    type="button"
                    onClick={() => {
                        setOpenId(null);
                        setReplying(false);
                    }}
                    aria-label="Back to messages list"
                    data-testid="messages-back-btn"
                    className="inline-flex items-center gap-1.5 min-h-[44px] px-3 -ml-1.5 rounded-xl text-[#0A3D7A] font-semibold hover:bg-[#0A3D7A]/5 transition-colors"
                >
                    <ArrowLeft className="w-5 h-5" aria-hidden="true" />
                    All messages
                </button>

                <article
                    data-testid={`message-detail-${open.id}`}
                    className="bg-white rounded-3xl p-6 border border-[#0A3D7A]/10 shadow-[0_4px_20px_rgba(10,61,122,0.06)] fade-in-up"
                >
                    <header className="flex items-center gap-3">
                        <Avatar seed={open.avatarSeed} name={open.sender} size={56} />
                        <div>
                            <p className="font-display font-bold text-lg text-[#1A1A2E] leading-tight">
                                {open.sender}
                            </p>
                            <p className="text-xs uppercase tracking-wider font-semibold text-[#B8860B]">
                                {open.senderTag} · {open.time} ago
                            </p>
                        </div>
                    </header>
                    <p className="mt-5 text-lg text-[#1A1A2E] leading-relaxed">
                        {open.full}
                    </p>
                    <div className="mt-5 grid grid-cols-2 gap-2">
                        <button
                            type="button"
                            onClick={() =>
                                speak(`Message from ${open.sender}. ${open.full}`)
                            }
                            data-testid="message-read-aloud-btn"
                            className="min-h-[56px] rounded-2xl bg-[#0A3D7A] hover:bg-[#082F5E] text-white text-base font-bold flex items-center justify-center gap-2 transition-colors"
                        >
                            <Volume2 className="w-5 h-5" aria-hidden="true" />
                            Read aloud
                        </button>
                        <button
                            type="button"
                            onClick={handleStartReply}
                            data-testid="message-reply-voice-btn"
                            aria-label={`Reply to ${open.sender} by voice`}
                            className="min-h-[56px] rounded-2xl bg-[#0A5C36] hover:bg-[#0a4d2d] text-white text-base font-bold flex items-center justify-center gap-2 transition-colors shadow-sm"
                        >
                            <MessageSquareReply className="w-5 h-5" aria-hidden="true" />
                            Reply by voice
                        </button>
                    </div>
                </article>

                {replying && (
                    <VoiceReply
                        recipient={open.sender}
                        threadId={open.id}
                        onSent={handleSent}
                        onClose={() => setReplying(false)}
                    />
                )}

                {openReplies.length > 0 && (
                    <section
                        data-testid={`message-replies-${open.id}`}
                        className="bg-white rounded-3xl p-5 border border-[#0A5C36]/20 shadow-[0_2px_12px_rgba(10,92,54,0.06)]"
                    >
                        <p className="text-xs uppercase tracking-wider font-semibold text-[#0A5C36] mb-3 flex items-center gap-1.5">
                            <CheckCircle2 className="w-4 h-4" aria-hidden="true" />
                            Sent replies
                        </p>
                        <ul className="space-y-3">
                            {openReplies.map((r) => (
                                <li
                                    key={r.id}
                                    data-testid={`reply-${r.id}`}
                                    className="flex items-start gap-2 rounded-2xl bg-[#E5F1EB] border border-[#0A5C36]/20 p-3"
                                >
                                    <SendIcon
                                        className="w-4 h-4 text-[#0A5C36] mt-1 shrink-0"
                                        aria-hidden="true"
                                    />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-base text-[#1A1A2E] leading-snug whitespace-pre-wrap">
                                            {r.text}
                                        </p>
                                        <p className="text-[11px] text-[#5A5A6E] mt-1 font-semibold uppercase tracking-wider">
                                            Sent · {formatRelative(r.ts)}
                                        </p>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </section>
                )}
            </div>
        );
    }

    return (
        <ul className="space-y-3" data-testid="messages-list">
            {MESSAGES.map((m) => {
                const replied = (repliesByMessage[m.id] || []).length > 0;
                return (
                    <li key={m.id}>
                        <button
                            type="button"
                            onClick={() => handleOpen(m)}
                            aria-label={`Message from ${m.sender}, ${m.time} ago: ${m.preview}${replied ? ". You have replied." : ""}`}
                            data-testid={`message-${m.id}`}
                            className="w-full bg-white rounded-2xl p-4 flex items-start gap-3 border border-[#0A3D7A]/10 shadow-[0_2px_12px_rgba(10,61,122,0.06)] hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(10,61,122,0.12)] hover:border-[#0A3D7A]/25 transition-all text-left min-h-[80px]"
                        >
                            <Avatar seed={m.avatarSeed} name={m.sender} size={48} />
                            <div className="flex-1 min-w-0">
                                <div className="flex items-baseline justify-between gap-2">
                                    <p className="font-display font-bold text-[#1A1A2E] truncate">
                                        {m.sender}
                                    </p>
                                    <p className="text-xs text-[#5A5A6E] shrink-0">
                                        {m.time}
                                    </p>
                                </div>
                                <p className="text-xs uppercase tracking-wider font-semibold text-[#B8860B] mt-0.5">
                                    {m.senderTag}
                                </p>
                                <p className="text-sm text-[#5A5A6E] mt-1 leading-snug line-clamp-2">
                                    {m.preview}
                                </p>
                                {replied && (
                                    <span
                                        data-testid={`replied-badge-${m.id}`}
                                        className="mt-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 bg-[#E5F1EB] text-[#0A5C36]"
                                    >
                                        <CheckCircle2 className="w-3 h-3" />
                                        Replied
                                    </span>
                                )}
                            </div>
                        </button>
                    </li>
                );
            })}
        </ul>
    );
};

export default Messages;
